import { parseQueryString } from "@client/lib/api";
import { addPrefix } from "@client/lib/object";
import { isEmailValid } from "@client/lib/validator";
import {
  mapFromBillingAddress,
  mapFromShippingAddress,
  mapToBillingAddress,
  mapToShippingAddress
} from "@common/lib/address";
import { isEqual } from "lodash";
import { type Checkout } from "./types";

export const getCheckoutOrderId = () => {
  const qs = parseQueryString();
  return qs.orderId ?? qs.o;
};

export const getPageFromHash = (): number | undefined => {
  const match = window.location.hash.match(/\#page(\d+)/);
  return match ? +match[1] : undefined;
};

const extractBaseInfo = (aoi: Checkout.AdjustOrderInfo): Checkout.AdjustOrderBaseInfo => ({
  billingEmail: aoi.billingEmail,
  eventId: aoi.eventId,
  eventDateLocationId: aoi.eventDateLocationId,
  includeOffer: aoi.includeOffer,
  billingCountryCode: aoi.billingCountryCode,
  billingSubdivisionCode: aoi.billingSubdivisionCode,
  offerProductVariantId: aoi.offerProductVariantId ?? null
});

const extractShippingInfo = (aoi: Checkout.AdjustOrderInfo): Checkout.AdjustOrderShippingInfo => ({
  shippingCountryCode: aoi.shippingCountryCode,
  shippingSubdivisionCode: aoi.shippingSubdivisionCode,
  shippingRuleId: aoi.shippingRuleId,
  shippingCity: aoi.shippingCity,
  pargoPickupPoint: aoi.pargoPickupPoint
  //isSubdivisionRequired: aoi.isSubdivisionRequired,
});

const shouldAdjustOrderBase = (prev: Checkout.AdjustOrderInfo, next: Checkout.AdjustOrderInfo): boolean => {
  const { billingEmail: pemail, ...pp } = extractBaseInfo(prev);
  const { billingEmail: nemail, ...np } = extractBaseInfo(next);

  if (isEqual(pp, np)) {
    if (pemail === nemail) return false;

    const isPrevEmailValid = !!pemail && isEmailValid(pemail);
    const isNextEmailValid = !!nemail && isEmailValid(nemail);

    if (!isNextEmailValid) return false;
    if (isPrevEmailValid !== isNextEmailValid) return true;
    if (isPrevEmailValid && isNextEmailValid && pemail !== nemail) return true;

    return false;
  }

  return true;
};

// assumption: shipping method is reset on shipping detail changes
const shouldAdjustOrderShipping = (prev: Checkout.AdjustOrderInfo, next: Checkout.AdjustOrderInfo): boolean => {
  // shipping unit can be ignore, as it doesn't affect shipping price
  const pp = extractShippingInfo(prev);
  const np = extractShippingInfo(next);

  const getIsFilledIn = (x: Checkout.AdjustOrderShippingInfo) => !!x.shippingCountryCode; // && (!x.isSubdivisionRequired || !!x.shippingSubdivisionCode);

  const pfi = getIsFilledIn(pp);
  const nfi = getIsFilledIn(np);

  return pp.shippingRuleId != np.shippingRuleId || (!isEqual(pp, np) && (pfi || nfi));
};

const hasValueChanged = (a: string | null, b: string | null): boolean => {
  if (!a && !b) return false;

  return a !== b;
};

export const shouldAdjustOrder = (prev: Checkout.AdjustOrderInfo, next: Checkout.AdjustOrderInfo): boolean =>
  shouldAdjustOrderBase(prev, next) ||
  shouldAdjustOrderShipping(prev, next) ||
  hasValueChanged(prev.productId, next.productId) ||
  hasValueChanged(prev.productVariantId, next.productVariantId) ||
  hasValueChanged(prev.currencyCode, next.currencyCode);

export const mapAddress = (address: Checkout.Api.UserAddress | null, prefix?: string): any => {
  if (address == null) return {};

  return Object.keys(address).reduce((res, x) => ({ ...res, [addPrefix(x, prefix)]: address[x] }), {});
};

export const mapBilling = (
  order: Checkout.ShippingInformation & Checkout.BillingInformation,
  requiresShipping: boolean
): Checkout.BillingInformationBase => {
  if (requiresShipping && order.isSameAsShipping) {
    return mapToBillingAddress(mapFromShippingAddress(order));
  }

  return mapToBillingAddress(mapFromBillingAddress(order));
};

export const mapShipping = (order: Checkout.Order): Partial<Checkout.ShippingInformation> => {
  if (order.pargoPickupPoint) {
    return {
      shippingPickupPointCode: order.pargoPickupPoint.pargoPointCode,
      shippingLatitude: order.pargoPickupPoint.latitude,
      shippingLongitude: order.pargoPickupPoint.longitude
    };
  }

  return mapToShippingAddress(mapFromShippingAddress(order));
};
